import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  GenericSystemActionCatalogAdapter,
  type GenericSystemActionCatalogEntryType,
  Resources,
  WorkflowTemplateApiVersion,
  type WorkflowTemplateApiVersionType,
  WorkflowTemplateType,
  WorkflowTemplatesAdapter,
  WorkflowTemplatesV2Adapter,
} from "@validereinc/domain";
import {
  DEFAULT_INVALIDATE_OPTIONS,
  useGetList,
  useGetOne,
  useUpdateOne,
  type UseMutationCallbackType,
} from "./adapterUtils";

export type UseUpdateWorkflowTemplateByApiVersionOptionsType = Omit<
  UseMutationCallbackType<
    Awaited<ReturnType<typeof WorkflowTemplatesAdapter.updateOne>>
  >,
  "customInvalidateFunction"
> & {
  templateId?: string;
};

export const invalidateWorkflowTemplateQueries = (
  queryClient: QueryClient,
  templateId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: [Resources.WORKFLOW_TEMPLATE],
    ...DEFAULT_INVALIDATE_OPTIONS,
  });

  if (templateId) {
    queryClient.invalidateQueries({
      queryKey: [Resources.WORKFLOW_TEMPLATE, "getOne", { id: templateId }],
      ...DEFAULT_INVALIDATE_OPTIONS,
    });
  }
};

export const useGetOneWorkflowTemplate = useGetOne<WorkflowTemplateType>(
  WorkflowTemplatesAdapter.getOne,
  Resources.WORKFLOW_TEMPLATE
);

export const useUpdateWorkflowTemplate = useUpdateOne<WorkflowTemplateType>(
  WorkflowTemplatesAdapter.updateOne,
  Resources.WORKFLOW_TEMPLATE
);

export const useUpdateWorkflowTemplateV2 = useUpdateOne<WorkflowTemplateType>(
  WorkflowTemplatesV2Adapter.updateOne,
  Resources.WORKFLOW_TEMPLATE
);

/** Routes workflow template updates to the v1 or v2 API based on template version. */
export const useUpdateWorkflowTemplateByApiVersion = (
  apiVersion?: WorkflowTemplateApiVersionType,
  options: UseUpdateWorkflowTemplateByApiVersionOptionsType = {}
) => {
  const queryClient = useQueryClient();
  const { templateId, ...mutationOptions } = options;

  const mutationOptionsWithInvalidation = {
    ...mutationOptions,
    customInvalidateFunction: () => {
      invalidateWorkflowTemplateQueries(queryClient, templateId);
    },
  };

  const updateWorkflowTemplateV1 = useUpdateWorkflowTemplate(
    mutationOptionsWithInvalidation
  );
  const updateWorkflowTemplateV2 = useUpdateWorkflowTemplateV2(
    mutationOptionsWithInvalidation
  );

  return apiVersion === WorkflowTemplateApiVersion.V2
    ? updateWorkflowTemplateV2
    : updateWorkflowTemplateV1;
};

export const useListWorkflowTemplates = useGetList(
  WorkflowTemplatesAdapter.getList,
  Resources.WORKFLOW_TEMPLATE
);

/** Fetch the generic system action catalog (allowlisted operations). */
export const useGenericSystemActionCatalog = (
  options: { enabled?: boolean } = {}
) =>
  useQuery<GenericSystemActionCatalogEntryType[]>({
    queryKey: [Resources.WORKFLOW_TEMPLATE, "genericSystemActionCatalog"],
    queryFn: GenericSystemActionCatalogAdapter.getList,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
